import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import validate from 'validate.js';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';

import { withAuctionCore } from 'agent';
import useStores from 'hooks/useStore';

import Container from 'components/common/Container/index';
import Button from 'components/common/Button';
import VinDecoder from 'components/VinDecoder';
import YMMLookup from 'components/Search/YMM';
import InspectionOptions from 'components/Search/InspectionOptions';
import DesktopBanner from 'components/common/DesktopBanner';

import useResponsive from 'hooks/useResponsive';
import useReports from 'hooks/useReports';
import useExternalReferrer from 'hooks/useExternalReferrer';
import vgpromo from 'assets/images/VGProLanding.png';
import constraints from './validation';

import {
  DesktopWrapper, ResetButton, MobileWrapper, VehicleDetailsContainer, Title, SubHeading, ErrorWrapper, ErrorText, Seperator, PromoImage, ButtonWrapper,
} from './styles';
import UserStore from '../../stores/UserStore';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const Search = observer(({ auctioncore }) => {
  const { UserStore, UIStore } = useStores();
  const { isLargeView } = useResponsive();
  const history = useHistory();
  const query = useQuery();
  const {
    vin,
    year,
    make,
    model,
    reset,
    setLoading,
    condition,
    setSelectedInspectionOptions,
    selectedInspectionOptions,
    mileage,
    setProperty,
  } = UIStore;

  const { token } = UserStore;
  const { setReports } = useReports(auctioncore);
  const [errors, setErrors] = useState([]);

  useEffect(() => {

    if (token) {
      useExternalReferrer(auctioncore, setProperty, query);
    }
  }, [token]);

  const validateForm = () => validate.async({
    year,
    make,
    model,
    mileage,
  }, constraints)
    .then(() => {
      setErrors([]);
      return true;
    })
    .catch((err) => {
      setErrors(err);
      return false;
    });

  useEffect(() => {
    if (errors?.year || errors?.model) validateForm();
  }, [year, make, model]);

  useEffect(() => {
    if (errors?.mileage) validateForm();
  }, [mileage]);

  const Submit = async () => {
    const formErrors = await validateForm();

    if (formErrors.length) {
      return;
    }
    setLoading(true);
    await setReports(condition);
    history.push('/results');
    setLoading(false);
  };

  const showResetButton = () => {
    if (vin) {
      return true;
    }

    return !!(year && make && model);
  };

  useEffect(() => {
    if (query.get('isNative') === 'true') {
      Submit();
    }
  }, []);

  return (
    <Container>
      {isLargeView && <DesktopBanner />}
      <DesktopWrapper>

        <Seperator>
          <SubHeading>Search by VIN or Make/Model</SubHeading>
          <Title>VIN</Title>
          <VinDecoder />
          <Title seperator>-OR-</Title>
          <Title>Year/Make/Model</Title>
          <YMMLookup
            errors={errors}
          />

          <InspectionOptions
            selectedModel={model}
            errors={errors}
            mileage={mileage}
          />

          <ButtonWrapper>
            {year && make && model && <ResetButton secondary onClick={() => reset()}>Reset</ResetButton>}
            {year && make && model && mileage && <Button onClick={() => Submit()}>Search</Button>}

          </ButtonWrapper>
        </Seperator>
        <Seperator><PromoImage src={vgpromo} border="0" /></Seperator>
      </DesktopWrapper>

      <MobileWrapper>
        <SubHeading>Search by VIN or Make/Model</SubHeading>
        <Title>VIN</Title>
        <VinDecoder />
        <Title seperator>-OR-</Title>
        <Title>Year/Make/Model</Title>
        <YMMLookup />

        <VehicleDetailsContainer>

          {!!(errors.year || errors.model || errors.make) && (
          <ErrorWrapper>
            <ErrorText>Enter a valid VIN or Y/M/M</ErrorText>
          </ErrorWrapper>
          )}

          <InspectionOptions
            selectedModel={model}
            errors={errors}
            setSelectedInspectionOptions={setSelectedInspectionOptions}
            selectedInspectionOptions={selectedInspectionOptions}
          />

        </VehicleDetailsContainer>
        <ButtonWrapper>
          {year && make && model && <ResetButton onClick={() => reset()}>Reset</ResetButton>}
          {year && make && model && mileage && <Button onClick={() => Submit()}>Search</Button>}

        </ButtonWrapper>

      </MobileWrapper>

      {!!(errors.year || errors.model || errors.make) && (
      <ErrorWrapper>
        <ErrorText>VIN or Y/M/M is required</ErrorText>
      </ErrorWrapper>
      )}

    </Container>
  );
});

Search.propTypes = {
  auctioncore: PropTypes.object.isRequired,
};

export default withAuctionCore(Search);
